import { ref, computed, Ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { asInt } from '@/helpers';
import { getConfigSendingChannelBox, ParentOperatorData } from './useSendingChannelForm';
import { TypeCampaignEnum } from '@/types';

export interface TemplatePreviewItem {
  id_template: number;
  type: string;
  label: string;
}

export interface TemplateConfigOptions {
  media: string;
  templateType?: string;
  campaignType: TypeCampaignEnum;
  initialTemplateId?: number | null;
  parentOperators?: ParentOperatorData[];
}

export interface VoucherConfigState {
  templatesContainsVoucher: Ref<boolean>;
  parentsOperatorAlreadyConfiguredVoucher: Ref<boolean>;
  parentsFilterHaveExpiredVoucherSegment: Ref<boolean>;
}

export const TEMPLATE_MEDIA_PREVIEW = {
  desktop: ['email', 'sms', 'richtexting', 'whatsapp', 'facebookmessenger', 'pushnotifications', 'popup', 'popupVoucher', 'popupNlForm', 'embed', 'embedNlForm', 'xSellsEmbed'],
  mobile: ['email', 'popup', 'popupVoucher', 'popupNlForm', 'embed', 'embedNlForm', 'xSellsEmbed'],
};

/**
 * Composable for managing template configuration in flow operators
 * Handles template selection, preview, and voucher configuration state
 */
export function useTemplateConfig(options: TemplateConfigOptions) {
  const { t } = useI18n();
  const idShop = ref(UserState.activeShop ? UserState.activeShop.id : 0);

  // Template list and selection state
  const listTemplates: Ref<TemplatesCustom[]> = ref([]);
  const templateSelectorIsOpen = ref(false);
  const typeTemplateSelector = ref(options.media);

  // Active preview state
  const activePreviewTemplate = ref<TemplatePreviewItem>({
    id_template: 0,
    type: options.media,
    label: '',
  });
  const activePreviewTemplateIndex = ref(0);
  const activePreviewMedia = ref<'desktop' | 'mobile'>('desktop');

  // Voucher configuration state
  const voucherConfig: VoucherConfigState = {
    templatesContainsVoucher: ref(false),
    parentsOperatorAlreadyConfiguredVoucher: ref(false),
    parentsFilterHaveExpiredVoucherSegment: ref(false),
  };

  // Image loading state for preview
  const imageLoadTime = ref<Record<number, number>>({});
  const imagesLoaded = ref<Record<number, boolean>>({});
  const imageRetryCount = ref<Record<number, number>>({});
  const tmpImgRefreshKey = ref(0);

  // Computed: Check if reuse previous coupon field should be displayed
  const showReusePreviousCouponField = computed(() => (
    voucherConfig.templatesContainsVoucher.value
    && (voucherConfig.parentsOperatorAlreadyConfiguredVoucher.value
      || voucherConfig.parentsFilterHaveExpiredVoucherSegment.value)
  ));

  // Computed: Check if voucher config should be displayed
  const showVoucherConfig = computed(() => {
    if (showReusePreviousCouponField.value) {
      return true; // Will be controlled by reuse_a_previous_coupon value in the component
    }
    return voucherConfig.templatesContainsVoucher.value;
  });

  // Computed: Check if automatic voucher method is allowed
  const allowAutomaticVoucherMethod = ref(true);

  /**
   * Select preview media (desktop/mobile)
   */
  const selectPreviewMedia = (type: 'desktop' | 'mobile') => {
    activePreviewMedia.value = type;
    tmpImgRefreshKey.value += 1;
  };

  /**
   * Handle image load success
   */
  const handleImageLoaded = (idTemplate: number) => {
    if (!imagesLoaded.value[idTemplate]) {
      imagesLoaded.value[idTemplate] = true;
      imageLoadTime.value[idTemplate] = new Date().getTime();
    }
  };

  /**
   * Handle image load failure with retry
   */
  const handleImageFailed = (idTemplate: number) => {
    if (!imageRetryCount.value[idTemplate]) {
      imageRetryCount.value[idTemplate] = 0;
    }

    if (imageRetryCount.value[idTemplate] >= 3) {
      return;
    }

    imageRetryCount.value[idTemplate] += 1;

    if (!imagesLoaded.value[idTemplate]) {
      setTimeout(() => {
        imageLoadTime.value[idTemplate] = new Date().getTime();
      }, 3000);
    }
  };

  /**
   * Handle active preview template change (tab change)
   */
  const handleActivePreviewTemplateChange = (index: number) => {
    activePreviewTemplateIndex.value = index;
    const template = listTemplates.value[index];
    if (!template) return;

    const templateType = template.type ?? options.media;
    activePreviewTemplate.value = {
      id_template: template.id_template,
      type: templateType,
      label: template.label ?? '',
    };

    // Adjust preview media if current selection is not available for this template type
    if (activePreviewMedia.value === 'mobile' && !TEMPLATE_MEDIA_PREVIEW.mobile.includes(templateType)) {
      selectPreviewMedia('desktop');
    } else if (activePreviewMedia.value === 'desktop' && !TEMPLATE_MEDIA_PREVIEW.desktop.includes(templateType)) {
      selectPreviewMedia('mobile');
    }
  };

  /**
   * Load template configuration from API
   */
  const loadTemplateConfig = async (idTemplate: number, parentOperators?: ParentOperatorData[]) => {
    const media = options.templateType || options.media;
    const parents = parentOperators ?? options.parentOperators ?? [];
    const config = await getConfigSendingChannelBox(idShop.value, idTemplate, media, parents);

    voucherConfig.templatesContainsVoucher.value = config.templatesContainsVoucher ?? false;
    voucherConfig.parentsOperatorAlreadyConfiguredVoucher.value = config.parentsOperatorAlreadyConfiguredVoucher ?? false;
    voucherConfig.parentsFilterHaveExpiredVoucherSegment.value = config.parentsFilterHaveExpiredVoucherSegment ?? false;
    listTemplates.value = config.listTemplate ?? [];

    // Set active preview to primary template
    const primaryTemplate = listTemplates.value[0];
    if (primaryTemplate) {
      activePreviewTemplate.value = {
        id_template: primaryTemplate.id_template,
        type: primaryTemplate.type ?? options.media,
        label: primaryTemplate.label ?? '',
      };
      activePreviewTemplateIndex.value = 0;

      // Check if automatic voucher method is allowed (not for bulk campaigns with voucher)
      const templateConfig = JSON.parse(primaryTemplate.template_config ?? '{}');
      const hasVoucher = templateConfig?.hasVoucher ?? false;
      if (hasVoucher && options.campaignType === TypeCampaignEnum.BULK) {
        allowAutomaticVoucherMethod.value = false;
      } else {
        allowAutomaticVoucherMethod.value = true;
      }

      // Set appropriate preview media
      if (TEMPLATE_MEDIA_PREVIEW.desktop.includes(activePreviewTemplate.value.type)) {
        selectPreviewMedia('desktop');
      } else {
        selectPreviewMedia('mobile');
      }
    }

    return config;
  };

  /**
   * Open template selector
   */
  const openTemplateSelector = (templateType?: string) => {
    templateSelectorIsOpen.value = true;
    if (templateType) {
      typeTemplateSelector.value = templateType;
    }
  };

  /**
   * Close template selector
   */
  const closeTemplateSelector = () => {
    templateSelectorIsOpen.value = false;
    typeTemplateSelector.value = activePreviewTemplate.value.type || options.media;
  };

  /**
   * Reset image preview state (useful after template edit)
   */
  const resetImagePreview = () => {
    imagesLoaded.value = {};
    imageLoadTime.value = {};
    tmpImgRefreshKey.value += 1;
  };

  /**
   * Initialize with existing template ID
   */
  const initialize = async (templateId?: number | null, parentOperators?: ParentOperatorData[]) => {
    if (templateId) {
      templateSelectorIsOpen.value = false;
      await loadTemplateConfig(asInt(templateId), parentOperators);
    } else {
      templateSelectorIsOpen.value = true;
    }
  };

  return {
    // State
    listTemplates,
    templateSelectorIsOpen,
    typeTemplateSelector,
    activePreviewTemplate,
    activePreviewTemplateIndex,
    activePreviewMedia,
    voucherConfig,
    imageLoadTime,
    tmpImgRefreshKey,
    allowAutomaticVoucherMethod,

    // Computed
    showReusePreviousCouponField,
    showVoucherConfig,

    // Methods
    selectPreviewMedia,
    handleImageLoaded,
    handleImageFailed,
    handleActivePreviewTemplateChange,
    loadTemplateConfig,
    openTemplateSelector,
    closeTemplateSelector,
    resetImagePreview,
    initialize,

    // Constants
    TEMPLATE_MEDIA_PREVIEW,
  };
}

export type TemplateConfig = ReturnType<typeof useTemplateConfig>;
